import Link from '@/components/compat/Link';
import { useEffect, useMemo, useState } from 'react';
import {
  ArrowLeft,
  ChevronDown,
  Copy,
  LayoutTemplate,
  PanelRightClose,
  PanelRightOpen,
  Plus,
  Trash2,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { painelInputClass, painelSelectTriggerClass } from '@/components/painel/cms/painel-form';
import {
  painelInsetCardClass,
  painelSurfaceClass,
  painelPageBackButtonClass,
  painelSelectContentClass,
  painelSwitchClass,
  painelButtonPrimaryClass,
  painelButtonOutlineClass,
} from '@/components/painel/painel-styles';
import { cn } from '@/lib/utils';
import type { ChangelogProductId } from '@/lib/changelog/changelog-sources';
import { useChangelogSources } from '@/hooks/use-changelog-sources';
import { ChangelogProductTabs } from '@/components/painel/changelog/changelog-product';
import { usePainelSaveFeedback } from '@/components/painel/PainelSaveFeedback';
import { usePainelSession } from '@/components/painel/rbac/painel-session-context';
import { ChangelogPageShell } from '@/components/painel/changelog/changelog-ui';
import { ChangelogRichEditor } from '@/components/painel/changelog/ChangelogRichEditor';
import { ChangelogEditorBlockPreview } from '@/components/painel/changelog/ChangelogEditorBlockPreview';
import { parseChangelogBlockHtml } from '@/components/painel/changelog/parse-changelog-block-html';
import type { ChangelogEntry, ChangelogItem, ChangelogSection, SectionType } from '@/lib/changelog/changelog-types';
import {
  CHANGELOG_SECTION_TYPE_ORDER,
  CHANGELOG_SECTION_VISUALS,
  getChangelogSectionVisual,
} from '@/lib/changelog/changelog-section-visuals';
import { CHANGELOG_PRODUCTS } from '@/components/painel/changelog/changelog-mock-data';
import { MOCK_PAINEL_USERS } from '@/lib/painel-rbac';
import * as changelogService from '@/services/changelog.service';
import { useRouter } from '@/lib/navigation';

type BlockState = {
  id: string;
  type: SectionType;
  title: string;
  accent: string;
  collapsed: boolean;
  html: string;
};

const PANEL_AUTHORS = MOCK_PAINEL_USERS.filter((u) =>
  u.username === 'cupim' || u.username === 'guinho'
);

function newId() {
  return typeof crypto !== 'undefined' ? crypto.randomUUID() : `${Date.now()}`;
}

function escapeHtml(s: string) {
  return s
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

function changelogItemsToHtml(items: ChangelogItem[]): string {
  const parts: string[] = [];
  for (const item of items) {
    parts.push(`<p><strong>${escapeHtml(item.title)}</strong></p>`);
    if (item.details?.length) {
      parts.push(`<ul>${item.details.map((d) => `<li>${escapeHtml(d)}</li>`).join('')}</ul>`);
    }
  }
  return parts.join('') || '<p></p>';
}

function formatChangelogDatePt(isoLocal: string): string {
  const d = new Date(isoLocal);
  if (Number.isNaN(d.getTime())) return isoLocal;
  return new Intl.DateTimeFormat('pt-BR', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  }).format(d);
}

function defaultTemplateBlocks(): BlockState[] {
  const t1 = CHANGELOG_SECTION_VISUALS.new;
  const t2 = CHANGELOG_SECTION_VISUALS.fix;
  return [
    {
      id: newId(),
      type: t1.type,
      title: t1.label,
      accent: t1.accent,
      collapsed: false,
      html: '<p>Descreva as novidades desta versão.</p>',
    },
    {
      id: newId(),
      type: t2.type,
      title: t2.label,
      accent: t2.accent,
      collapsed: true,
      html: '<ul><li>Ajustes de regressão aqui.</li></ul>',
    },
  ];
}

function blocksFromPublicEntry(entry: ChangelogEntry): BlockState[] {
  return entry.sections.map((s) => {
    const visual = getChangelogSectionVisual(s.type);
    return {
      id: newId(),
      type: s.type,
      title: s.label,
      accent: visual.accent,
      collapsed: false,
      html: changelogItemsToHtml(s.items),
    };
  });
}

function blocksToSections(blocks: BlockState[]): ChangelogSection[] {
  return blocks.map((b) => {
    const parsed = parseChangelogBlockHtml(b.html);
    const items: ChangelogItem[] = parsed.map((p) => ({
      title: p.title,
      ...(p.details.length > 0
        ? { details: p.details }
        : p.inlineDescription
          ? { details: [p.inlineDescription] }
          : {}),
    }));
    return {
      type: b.type,
      label: b.title.trim() || getChangelogSectionVisual(b.type).label,
      count: items.length,
      items,
    };
  });
}

function makeBlockFromType(type: SectionType): BlockState {
  const visual = getChangelogSectionVisual(type);
  return {
    id: newId(),
    type,
    title: visual.label,
    accent: visual.accent,
    collapsed: false,
    html: '<p>Conteúdo do bloco…</p>',
  };
}

export function PainelChangelogEditorPage({
  product: changelogProduct,
  mode,
  versionId,
}: {
  product: ChangelogProductId;
  mode: 'new' | 'edit';
  versionId?: string;
}) {
  const router = useRouter();
  const { showSaveSuccess } = usePainelSaveFeedback();
  const { session } = usePainelSession();
  const { sources, reload } = useChangelogSources();
  const sourceEntry = useMemo(
    () =>
      mode === 'edit' && versionId
        ? sources[changelogProduct].entries.find((e) => e.version === versionId)
        : undefined,
    [mode, versionId, changelogProduct, sources]
  );

  const defaultAuthorUsername = useMemo(() => {
    const fromSession = PANEL_AUTHORS.find((u) => u.username === session?.username);
    return fromSession?.username ?? PANEL_AUTHORS[0]?.username ?? 'cupim';
  }, [session?.username]);

  const [version, setVersion] = useState('');
  const [title, setTitle] = useState('');
  const [summary, setSummary] = useState('');
  const [releaseDate, setReleaseDate] = useState(() => {
    const now = new Date();
    now.setMinutes(now.getMinutes() - now.getTimezoneOffset());
    return now.toISOString().slice(0, 16);
  });
  const [productLine, setProductLine] = useState<string>(() =>
    changelogProduct === 'ajuda' ? 'xat Ajuda' : 'xat Chat'
  );
  const [blocks, setBlocks] = useState<BlockState[]>(() => defaultTemplateBlocks());

  const [publishStatus, setPublishStatus] = useState<'draft' | 'scheduled' | 'published'>('draft');
  const [visibility, setVisibility] = useState<'public' | 'unlisted'>('public');
  const [featured, setFeatured] = useState(false);
  const [pinHome, setPinHome] = useState(false);
  const [slug, setSlug] = useState('');
  const [tags, setTags] = useState('web,big-update,glass');
  const [authorUsername, setAuthorUsername] = useState(defaultAuthorUsername);
  const [dbVersionId, setDbVersionId] = useState<number | null>(null);
  const [saving, setSaving] = useState(false);
  const [hydratedFromSource, setHydratedFromSource] = useState(mode === 'new');
  const [lastUpdatedAt, setLastUpdatedAt] = useState<Date | null>(null);
  const [previewOpenByBlock, setPreviewOpenByBlock] = useState<Record<string, boolean>>({});

  useEffect(() => {
    setAuthorUsername(defaultAuthorUsername);
  }, [defaultAuthorUsername]);

  useEffect(() => {
    if (mode !== 'edit' || !sourceEntry || hydratedFromSource) return;
    setVersion(sourceEntry.version);
    setTitle(sourceEntry.title?.trim() || `Changelog v${sourceEntry.version}`);
    setSummary(sourceEntry.date);
    setBlocks(blocksFromPublicEntry(sourceEntry));
    setPublishStatus('published');
    setFeatured(!!sourceEntry.latest);
    setSlug(sourceEntry.version.replace(/\./g, '-'));
    if (typeof sourceEntry.id === 'number') setDbVersionId(sourceEntry.id);
    const metaAuthor =
      typeof sourceEntry.meta?.authorUsername === 'string'
        ? sourceEntry.meta.authorUsername
        : null;
    if (metaAuthor && PANEL_AUTHORS.some((u) => u.username === metaAuthor)) {
      setAuthorUsername(metaAuthor);
    }
    setHydratedFromSource(true);
  }, [mode, sourceEntry, hydratedFromSource]);

  useEffect(() => {
    if (mode !== 'edit' || !versionId || dbVersionId != null) return;
    let cancelled = false;
    void (async () => {
      try {
        const products = await changelogService.getAdminProducts();
        const product = products.find((p) => p.slug === changelogProduct || p.id === changelogProduct);
        if (!product?.productId) return;
        const rows = await changelogService.getAdminVersions(product.productId);
        const match = rows.find((r) => r.version === versionId);
        if (!cancelled && match?.id != null) setDbVersionId(match.id);
      } catch {
        /* API opcional em dev */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [mode, versionId, changelogProduct, dbVersionId]);

  const author = useMemo(
    () => PANEL_AUTHORS.find((u) => u.username === authorUsername) ?? PANEL_AUTHORS[0],
    [authorUsername]
  );

  const addBlockFromType = (type: SectionType) => {
    setBlocks((prev) => [...prev, makeBlockFromType(type)]);
  };

  const duplicateStructure = () => {
    setBlocks((prev) =>
      prev.map((b) => ({
        ...b,
        id: newId(),
        html: b.html,
      }))
    );
    showSaveSuccess({
      title: 'Estrutura duplicada',
      description: 'As secções foram clonadas neste editor (ainda não publicadas).',
    });
  };

  const buildPayloadBody = (status: typeof publishStatus) => {
    const sections = blocksToSections(blocks);
    const dateLabel = summary.trim() || formatChangelogDatePt(releaseDate);
    return {
      date: dateLabel,
      sections,
      latest: featured && status === 'published',
    };
  };

  const buildMeta = (status: typeof publishStatus) => ({
    latest: featured && status === 'published',
    status,
    visibility,
    pinHome,
    slug: slug.trim() || version.replace(/\./g, '-'),
    tags: tags
      .split(',')
      .map((t) => t.trim())
      .filter(Boolean),
    authorUsername: author?.username ?? authorUsername,
    authorName: author?.name ?? 'Cupim',
    productLine,
  });

  const persistVersion = async (status: typeof publishStatus) => {
    const versionNumber = version.trim().replace(/^v/i, '');
    if (!versionNumber) {
      showSaveSuccess({
        title: 'Versão em falta',
        description: 'Indique o número da versão antes de guardar.',
      });
      return;
    }

    setSaving(true);
    try {
      const products = await changelogService.getAdminProducts();
      const product = products.find((p) => p.slug === changelogProduct || p.id === changelogProduct);
      if (!product?.productId) {
        throw new Error('Produto de changelog não encontrado na API.');
      }

      const titleValue = title.trim() || `Changelog v${versionNumber}`;
      const body = buildPayloadBody(status);
      const meta = buildMeta(status);
      const releasedAt =
        status === 'published' || status === 'scheduled'
          ? new Date(releaseDate).toISOString()
          : undefined;

      if (mode === 'edit' && dbVersionId != null) {
        await changelogService.updateVersion(dbVersionId, {
          version: versionNumber,
          title: titleValue,
          body,
          released_at: releasedAt,
          meta,
        });
      } else if (mode === 'edit' && versionId) {
        const rows = await changelogService.getAdminVersions(product.productId);
        const match = rows.find((r) => r.version === versionId);
        if (match?.id != null) {
          await changelogService.updateVersion(match.id, {
            version: versionNumber,
            title: titleValue,
            body,
            released_at: releasedAt,
            meta,
          });
          setDbVersionId(match.id);
        } else {
          const created = (await changelogService.createVersion({
            changelog_product_id: product.productId,
            version: versionNumber,
            title: titleValue,
            body,
            released_at: releasedAt,
            meta,
          })) as { id?: number };
          if (created?.id != null) setDbVersionId(created.id);
        }
      } else {
        const created = (await changelogService.createVersion({
          changelog_product_id: product.productId,
          version: versionNumber,
          title: titleValue,
          body,
          released_at: releasedAt,
          meta,
        })) as { id?: number };
        if (created?.id != null) setDbVersionId(created.id);
      }

      setPublishStatus(status);
      setLastUpdatedAt(new Date());
      await reload();

      showSaveSuccess({
        title: status === 'published' ? 'Versão publicada' : 'Rascunho guardado',
        description:
          status === 'published'
            ? `v${versionNumber} está disponível em /changelog (${author?.name ?? 'equipa'}).`
            : `Rascunho de v${versionNumber} guardado por ${author?.name ?? 'equipa'}.`,
      });

      if (mode === 'new' && status === 'published') {
        router.push(`/painel/changelog/${changelogProduct}/versions/${versionNumber}`);
      }
    } catch (err) {
      console.error('[changelog] falha ao guardar versão', err);
      showSaveSuccess({
        title: 'Não foi possível guardar',
        description:
          err instanceof Error
            ? err.message
            : 'Verifique a API do changelog e tente novamente.',
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <ChangelogPageShell className="space-y-6 pb-12">
      <ChangelogProductTabs product={changelogProduct} />

      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <Button variant="ghost" asChild className={painelPageBackButtonClass}>
            <Link href={`/painel/changelog/${changelogProduct}/versions`}>
              <ArrowLeft className="mr-2 h-4 w-4" />
              Versões
            </Link>
          </Button>
          <p className="font-inter text-[10px] font-bold uppercase tracking-[0.2em] text-sky-300/55">
            Editor · {sources[changelogProduct].label}
          </p>
          <h1 className="mt-3 font-poppins text-2xl font-bold tracking-tight text-white md:text-3xl">
            {mode === 'new'
              ? 'Nova versão'
              : sourceEntry
                ? `Editando v${sourceEntry.version}`
                : `Editando ${versionId ?? ''}`}
          </h1>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button
            type="button"
            variant="outline"
            className={cn(painelButtonOutlineClass, 'rounded-xl')}
            onClick={duplicateStructure}
            disabled={saving}
          >
            <Copy className="mr-2 h-4 w-4" /> Duplicar estrutura
          </Button>
          <Button
            type="button"
            className="rounded-xl bg-gradient-to-r from-emerald-500 to-sky-500 font-poppins font-semibold hover:brightness-105"
            onClick={() => {
              const publicPath =
                changelogProduct === 'ajuda' ? '/changelog?product=ajuda' : '/changelog';
              window.open(publicPath, '_blank', 'noopener,noreferrer');
            }}
          >
            Pré-visualizar
          </Button>
          <Button
            type="button"
            variant="outline"
            className={cn(painelButtonOutlineClass, 'rounded-xl')}
            disabled={saving}
            onClick={() => void persistVersion('draft')}
          >
            Salvar rascunho
          </Button>
          <Button
            type="button"
            className={painelButtonPrimaryClass}
            disabled={saving}
            onClick={() => void persistVersion('published')}
          >
            {saving ? 'A publicar…' : 'Publicar'}
          </Button>
        </div>
      </div>

      <div className="grid gap-6 xl:grid-cols-[minmax(0,1fr)_340px]">
        <div className="space-y-6">
          <section className={cn(painelSurfaceClass, 'space-y-4 p-6')}>
            <div className="grid gap-4 md:grid-cols-2">
              <div className="grid gap-2">
                <Label className="text-blue-100/90">Version number</Label>
                <Input
                  value={version}
                  onChange={(e) => {
                    const v = e.target.value;
                    setVersion(v);
                    if (!slug || slug === version.replace(/\./g, '-')) {
                      setSlug(v.replace(/^v/i, '').replace(/\./g, '-'));
                    }
                  }}
                  className={painelInputClass}
                  placeholder="1.70"
                />
              </div>
              <div className="grid gap-2">
                <Label className="text-blue-100/90">Product</Label>
                <Select value={productLine} onValueChange={setProductLine}>
                  <SelectTrigger className={painelSelectTriggerClass}>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className={painelSelectContentClass}>
                    {CHANGELOG_PRODUCTS.map((p) => (
                      <SelectItem key={p} value={p}>
                        {p}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid gap-2">
              <Label className="text-blue-100/90">Release title</Label>
              <Input value={title} onChange={(e) => setTitle(e.target.value)} className={painelInputClass} />
            </div>
            <div className="grid gap-2">
              <Label className="text-blue-100/90">Short summary / data pública</Label>
              <textarea
                value={summary}
                onChange={(e) => setSummary(e.target.value)}
                rows={3}
                className={cn(painelInputClass, 'min-h-[92px]')}
                placeholder="Ex.: 5 de novembro de 2025"
              />
            </div>
            <div className="grid gap-2">
              <Label className="text-blue-100/90">Release date</Label>
              <Input
                type="datetime-local"
                value={releaseDate.slice(0, 16)}
                onChange={(e) => setReleaseDate(e.target.value)}
                className={painelInputClass}
              />
            </div>
          </section>

          <div className="flex flex-wrap items-center gap-2">
            <span className="font-poppins text-sm font-semibold text-white">Modelos rápidos</span>
            {CHANGELOG_SECTION_TYPE_ORDER.map((type) => {
              const visual = CHANGELOG_SECTION_VISUALS[type];
              const Icon = visual.icon;
              return (
                <button
                  key={type}
                  type="button"
                  className="inline-flex items-center gap-2 rounded-xl border border-white/10 bg-white/[0.04] px-3 py-2 text-[13px] text-blue-100 transition hover:border-sky-400/35 hover:bg-white/[0.06]"
                  onClick={() => addBlockFromType(type)}
                >
                  <Icon className="h-4 w-4" style={{ color: visual.accent }} aria-hidden />
                  {visual.label}
                </button>
              );
            })}
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="ml-auto rounded-xl border-dashed border-sky-400/35 text-sky-100"
              onClick={() => addBlockFromType('new')}
            >
              <LayoutTemplate className="mr-2 h-4 w-4" /> Duplicar seção mestre
            </Button>
          </div>

          <div className="space-y-4">
            {blocks.map((b) => {
              const visual = getChangelogSectionVisual(b.type);
              const Icon = visual.icon;
              return (
                <div
                  key={b.id}
                  className={cn(
                    painelSurfaceClass,
                    'overflow-hidden border-white/12 bg-gradient-to-b from-white/[0.05] to-transparent'
                  )}
                  style={{ boxShadow: `0 24px 64px -32px ${b.accent}55` }}
                >
                  <div className="flex w-full items-center gap-3 border-b border-white/10 px-4 py-3 text-left">
                    <button
                      type="button"
                      aria-expanded={!b.collapsed}
                      aria-label={b.collapsed ? 'Expandir secção' : 'Recolher secção'}
                      className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg text-slate-300 transition-colors hover:bg-white/[0.08]"
                      onClick={() =>
                        setBlocks((prev) =>
                          prev.map((x) => (x.id === b.id ? { ...x, collapsed: !x.collapsed } : x))
                        )
                      }
                    >
                      <ChevronDown
                        className={cn('h-5 w-5 transition-transform', b.collapsed && '-rotate-90')}
                      />
                    </button>
                    <span
                      className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-lg"
                      style={{ backgroundColor: `${b.accent}28` }}
                    >
                      <Icon className="h-4 w-4" style={{ color: b.accent }} aria-hidden />
                    </span>
                    <div className="min-w-0 flex-1">
                      <Label className="text-[11px] uppercase tracking-wider text-slate-400">
                        Section title
                      </Label>
                      <Input
                        value={b.title}
                        onChange={(e) =>
                          setBlocks((prev) =>
                            prev.map((x) => (x.id === b.id ? { ...x, title: e.target.value } : x))
                          )
                        }
                        className={cn(painelInputClass, 'mt-1')}
                      />
                    </div>
                    <Button
                      size="sm"
                      type="button"
                      variant="ghost"
                      className="shrink-0 text-rose-300 hover:bg-rose-500/15"
                      onClick={() => setBlocks((prev) => prev.filter((x) => x.id !== b.id))}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>

                  {!b.collapsed ? (
                    <div
                      className={cn(
                        'grid gap-6 p-4 pt-6 lg:items-start',
                        previewOpenByBlock[b.id] ? 'lg:grid-cols-2' : 'lg:grid-cols-1'
                      )}
                    >
                      <div className="min-w-0 space-y-4">
                        <div className="flex flex-wrap items-end gap-3">
                          <div className="grid min-w-[12rem] flex-1 gap-2">
                            <Label className="text-blue-100/90">Tipo de secção</Label>
                            <Select
                              value={b.type}
                              onValueChange={(v) => {
                                const type = v as SectionType;
                                const next = getChangelogSectionVisual(type);
                                setBlocks((prev) =>
                                  prev.map((x) =>
                                    x.id === b.id
                                      ? {
                                          ...x,
                                          type,
                                          accent: next.accent,
                                          title:
                                            x.title === getChangelogSectionVisual(x.type).label
                                              ? next.label
                                              : x.title,
                                        }
                                      : x
                                  )
                                );
                              }}
                            >
                              <SelectTrigger className={painelSelectTriggerClass}>
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent className={painelSelectContentClass}>
                                {CHANGELOG_SECTION_TYPE_ORDER.map((type) => {
                                  const opt = CHANGELOG_SECTION_VISUALS[type];
                                  const OptIcon = opt.icon;
                                  return (
                                    <SelectItem key={type} value={type}>
                                      <span className="inline-flex items-center gap-2">
                                        <OptIcon className="h-3.5 w-3.5" style={{ color: opt.accent }} />
                                        {opt.label}
                                      </span>
                                    </SelectItem>
                                  );
                                })}
                              </SelectContent>
                            </Select>
                          </div>
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            className={cn(
                              painelButtonOutlineClass,
                              'h-10 shrink-0 rounded-xl gap-2'
                            )}
                            onClick={() =>
                              setPreviewOpenByBlock((prev) => ({
                                ...prev,
                                [b.id]: !prev[b.id],
                              }))
                            }
                          >
                            {previewOpenByBlock[b.id] ? (
                              <>
                                <PanelRightClose className="h-4 w-4" />
                                Ocultar prévia
                              </>
                            ) : (
                              <>
                                <PanelRightOpen className="h-4 w-4" />
                                Prévia ao vivo
                              </>
                            )}
                          </Button>
                        </div>
                        <div>
                          <Label className="mb-2 block text-blue-100/90">Conteúdo (rich text)</Label>
                          <div
                            className="rounded-2xl border border-white/10 bg-black/30 p-2"
                            style={{ borderColor: `${b.accent}55` }}
                          >
                            <ChangelogRichEditor
                              value={b.html}
                              onChange={(html) =>
                                setBlocks((prev) =>
                                  prev.map((x) => (x.id === b.id ? { ...x, html } : x))
                                )
                              }
                              resetKey={b.id}
                              minHeightClass="min-h-[220px]"
                            />
                          </div>
                        </div>
                      </div>
                      {previewOpenByBlock[b.id] ? (
                        <div className="min-w-0 space-y-2 lg:sticky lg:top-20 lg:self-start">
                          <p className="font-inter text-[10px] font-bold uppercase tracking-[0.18em] text-sky-300/45">
                            Pré-visualização ao vivo
                          </p>
                          <ChangelogEditorBlockPreview
                            block={{ title: b.title, type: b.type, accent: b.accent, html: b.html }}
                          />
                          <p className="font-inter text-[11px] leading-snug text-slate-500">
                            Use parágrafos com <strong className="text-slate-400">negrito</strong> para cada
                            item e listas para detalhes — igual ao changelog público. Menções{' '}
                            <code className="rounded bg-white/10 px-1 font-mono text-[10px] text-sky-300/90">
                              [[power]]
                            </code>{' '}
                            viram links interativos na prévia.
                          </p>
                        </div>
                      ) : null}
                    </div>
                  ) : null}
                </div>
              );
            })}
            <Button
              type="button"
              variant="outline"
              className="w-full rounded-2xl border-dashed border-sky-400/35 bg-white/[0.02] py-8 font-poppins text-sky-100 hover:bg-sky-500/10"
              onClick={() => addBlockFromType('new')}
            >
              <Plus className="mr-2 inline h-4 w-4" /> Nova seção
            </Button>
          </div>
        </div>

        <aside className="sticky top-6 h-fit space-y-4">
          <div className={cn(painelSurfaceClass, 'space-y-4 p-5')}>
            <h3 className="font-poppins text-sm font-semibold text-white">Settings</h3>
            <div className="grid gap-3">
              <Label className="text-blue-100/90 text-xs uppercase tracking-wider">Publish status</Label>
              <Select
                value={publishStatus}
                onValueChange={(v) => setPublishStatus(v as typeof publishStatus)}
              >
                <SelectTrigger className={painelSelectTriggerClass}>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className={painelSelectContentClass}>
                  <SelectItem value="draft">Rascunho</SelectItem>
                  <SelectItem value="scheduled">Agendado</SelectItem>
                  <SelectItem value="published">Publicado</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="grid gap-3">
              <Label className="text-blue-100/90 text-xs uppercase tracking-wider">Visibility</Label>
              <Select
                value={visibility}
                onValueChange={(v) => setVisibility(v as typeof visibility)}
              >
                <SelectTrigger className={painelSelectTriggerClass}>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className={painelSelectContentClass}>
                  <SelectItem value="public">Público</SelectItem>
                  <SelectItem value="unlisted">Não listado</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-center justify-between rounded-xl border border-white/10 bg-white/[0.03] px-3 py-2">
              <span className="font-inter text-sm text-blue-100/90">Featured release</span>
              <Switch checked={featured} onCheckedChange={setFeatured} className={painelSwitchClass} />
            </div>
            <div className="flex items-center justify-between rounded-xl border border-white/10 bg-white/[0.03] px-3 py-2">
              <span className="font-inter text-sm text-blue-100/90">Pin to homepage</span>
              <Switch checked={pinHome} onCheckedChange={setPinHome} className={painelSwitchClass} />
            </div>
          </div>

          <div className={cn(painelSurfaceClass, 'space-y-4 p-5')}>
            <h3 className="font-poppins text-sm font-semibold text-white">Slug & Tags</h3>
            <div className="grid gap-2">
              <Label className="text-blue-100/90 text-xs">Autor</Label>
              <Select value={authorUsername} onValueChange={setAuthorUsername}>
                <SelectTrigger className={painelSelectTriggerClass}>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className={painelSelectContentClass}>
                  {PANEL_AUTHORS.map((u) => (
                    <SelectItem key={u.id} value={u.username}>
                      {u.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="grid gap-2">
              <Label className="text-blue-100/90 text-xs">Slug</Label>
              <Input value={slug} onChange={(e) => setSlug(e.target.value)} className={painelInputClass} />
            </div>
            <div className="grid gap-2">
              <Label className="text-blue-100/90 text-xs">Tags (separadas por vírgula)</Label>
              <Input value={tags} onChange={(e) => setTags(e.target.value)} className={painelInputClass} />
            </div>
            <div className="grid gap-1 text-xs">
              <div className="flex justify-between text-slate-400">
                <span>Author</span>
                <span className="text-blue-50">{author?.name ?? '—'}</span>
              </div>
              <div className="flex justify-between text-slate-400">
                <span>Last updated</span>
                <span className="text-blue-50">
                  {lastUpdatedAt
                    ? lastUpdatedAt.toLocaleString('pt-BR', {
                        day: '2-digit',
                        month: 'short',
                        hour: '2-digit',
                        minute: '2-digit',
                      })
                    : '—'}
                </span>
              </div>
              <div className="flex justify-between text-slate-400">
                <span>Est. reading</span>
                <span className="text-blue-50">≈ {(blocks.length * 1.8 + 1).toFixed(1)} min</span>
              </div>
            </div>
          </div>

          <div className={cn(painelSurfaceClass, 'space-y-4 p-5')}>
            <h3 className="font-poppins text-sm font-semibold text-white">SEO preview</h3>
            <div className={cn(painelInsetCardClass, 'space-y-1 border-white/10 p-4 text-left')}>
              <p className="font-inter text-[11px] text-emerald-300/95">
                xatajuda.com › changelog › {slug || 'slug'}
              </p>
              <p className="font-inter text-[15px] font-semibold text-sky-200 hover:underline">
                [{version || '…'}] {title || 'Untitled changelog'}
              </p>
              <p className="line-clamp-2 font-inter text-[13px] text-slate-400">
                {summary || 'Resumo será exibido aqui nos resultados de busca.'}
              </p>
            </div>
          </div>
        </aside>
      </div>
    </ChangelogPageShell>
  );
}
